import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, Lock, Calendar, Link as LinkIcon, CheckCircle, XCircle } from "lucide-react";

/**
 * Developer Documentation Page for RSVP Feature Design Decisions
 * 
 * This page documents the various options and decision points for the RSVP feature,
 * including password protection, time-gating, and short-link access control.
 * 
 * Access: Controlled by VITE_ENABLE_DEV_DOCS environment variable
 */
export default function RSVPDocumentationPage() {
  const [activeFlow, setActiveFlow] = useState<string | null>(null);

  const flowcharts = [
    {
      id: "access-control",
      title: "RSVP Access Control Flow",
      description: "Complete flow from page access to RSVP submission",
      mermaid: `flowchart TD
    A[User Accesses Public Event Page] --> B{Password Protected?}
    B -->|Yes| C[Show Password Form]
    B -->|No| D[Show Event Page]
    C --> E{Password Correct?}
    E -->|No| C
    E -->|Yes| D
    D --> F{RSVP Enabled?}
    F -->|No| G[Show Event Only]
    F -->|Yes| H{Access Method?}
    H -->|Public Link| I{RSVP Deadline Passed?}
    H -->|Short Link| J{Short Link Valid?}
    J -->|No| K[Show Error: Invalid Link]
    J -->|Yes| L{Short Link Expired?}
    L -->|Yes| K
    L -->|No| M[Track Visit]
    M --> I
    I -->|Yes| N[Show RSVP Closed Message]
    I -->|No| O[Show RSVP Form]
    O --> P[User Submits RSVP]
    P --> Q[Save Response]
    Q --> R[Send Notification if Enabled]`,
    },
    {
      id: "password-protection",
      title: "Password Protection Options",
      description: "Different approaches to password protecting event pages",
      mermaid: `flowchart LR
    A[Password Protection] --> B[Option 1: Simple Password]
    A --> C[Option 2: Per-Guest Passwords]
    A --> D[Option 3: Password + Short Link]
    B --> B1[Single password for all guests]
    B --> B2[Stored hashed in database]
    B --> B3[Session-based access]
    C --> C1[Unique password per guest]
    C --> C2[Generated from guest email/ID]
    C --> C3[Pre-filled in short link]
    D --> D1[Password required first]
    D --> D2[Then short link validation]
    D --> D3[Maximum security]`,
    },
    {
      id: "time-gating",
      title: "RSVP Time-Gating Options",
      description: "How to control when RSVP is available",
      mermaid: `flowchart TD
    A[Time-Gating Options] --> B[Option 1: Deadline Only]
    A --> C[Option 2: Start + Deadline]
    A --> D[Option 3: Multiple Deadlines]
    B --> B1[RSVP closes on deadline]
    B --> B2[Show countdown timer]
    B --> B3[After deadline: show closed message]
    C --> C1[RSVP opens on start date]
    C --> C2[RSVP closes on deadline]
    C --> C3[Show 'Coming Soon' before start]
    D --> D1[Early bird deadline]
    D --> D2[Standard deadline]
    D --> D3[Late deadline with penalty]`,
    },
    {
      id: "short-links",
      title: "Short Link Access Control",
      description: "Options for tracking and controlling access via short links",
      mermaid: `flowchart TD
    A[Short Link Options] --> B[Option 1: No Expiration]
    A --> C[Option 2: Time-Based Expiration]
    A --> D[Option 3: Usage-Based Expiration]
    A --> E[Option 4: Guest-Specific Links]
    B --> B1[Link works forever]
    B --> B2[Track visits only]
    B --> B3[Can be deactivated manually]
    C --> C1[Expires after X days]
    C --> C2[Expires on specific date]
    C --> C3[Show expiration warning]
    D --> D1[Expires after N uses]
    D --> D2[One-time use option]
    D --> D3[Track remaining uses]
    E --> E1[One link per guest]
    E --> E2[Pre-fills guest info]
    E --> E3[Can't be shared effectively]`,
    },
    {
      id: "rsvp-form",
      title: "RSVP Form Submission Flow",
      description: "What happens when a user submits an RSVP",
      mermaid: `flowchart TD
    A[User Clicks RSVP Button] --> B[Load RSVP Form]
    B --> C{Form Type?}
    C -->|Simple| D[Name + Email + Attending]
    C -->|Full| E[Full Guest Details Form]
    D --> F[Validate Input]
    E --> F
    F --> G{Validation Passed?}
    G -->|No| H[Show Errors]
    H --> B
    G -->|Yes| I[Submit to API]
    I --> J{Submission Success?}
    J -->|No| K[Show Error Message]
    K --> B
    J -->|Yes| L{Email Notifications Enabled?}
    L -->|Yes| M[Send Email to Event Owner]
    L -->|No| N[Show Success Message]
    M --> N
    N --> O[Update Guest List]
    O --> P[Show Confirmation]`,
    },
    {
      id: "decision-tree",
      title: "RSVP Feature Decision Tree",
      description: "Key decisions to make when implementing RSVP",
      mermaid: `flowchart TD
    A[Start: RSVP Feature Design] --> B{Need Password Protection?}
    B -->|Yes| C[Implement Password System]
    B -->|No| D[Skip Password]
    C --> E{Need Time-Gating?}
    D --> E
    E -->|Yes| F[Implement Deadline System]
    E -->|No| G[RSVP Always Available]
    F --> H{Need Short Links?}
    G --> H
    H -->|Yes| I[Implement Short Link System]
    H -->|No| J[Public RSVP Only]
    I --> K{Short Link Expiration?}
    K -->|Yes| L[Add Expiration Logic]
    K -->|No| M[Links Never Expire]
    L --> N[Final Implementation]
    M --> N
    J --> N
    N --> O[Test & Deploy]`,
    },
  ];

  const currentOptions = [
    {
      feature: "Password Protection",
      status: "implemented",
      details: [
        "✅ Basic password protection exists (`passwordEnabled`, `event_password_enabled`)",
        "✅ Password stored hashed in database",
        "⚠️ Session-based access (needs verification)",
        "❌ Per-guest passwords not implemented",
        "❌ Password + short link combination not implemented",
      ],
    },
    {
      feature: "Time-Gating (RSVP Deadline)",
      status: "partially-implemented",
      details: [
        "✅ Deadline date field exists (`rs_date` in `rsvp_responses` table - this is the event-level deadline, not per-guest)",
        "✅ Can be set via RSVP settings API (`dueDate` parameter)",
        "⚠️ Note: `rsvp_responses` table stores RSVP SETTINGS (one per event), not individual guest responses",
        "⚠️ Individual guest submission dates are stored in `guests.guest_submitted_at`",
        "❌ Frontend validation not implemented (checking if current date > deadline)",
        "❌ Countdown timer not shown",
        "❌ Closed message after deadline not implemented",
        "❌ Start date not implemented (only deadline)",
      ],
    },
    {
      feature: "Short Links",
      status: "implemented",
      details: [
        "✅ Short link generation exists (`ShortLinkController`)",
        "✅ Visit tracking (`short_link_visits`, `short_link_last_visited_at`)",
        "✅ Active/inactive toggle (`short_link_active`)",
        "✅ Guest-specific links (`short_link_guest_id`)",
        "❌ Expiration date not implemented",
        "❌ Usage-based expiration not implemented",
        "❌ Pre-filled guest info not implemented",
      ],
    },
    {
      feature: "RSVP Form",
      status: "partially-implemented",
      details: [
        "✅ Event owner RSVP form fully implemented (`RsvpModal.tsx` in GuestsTab)",
        "✅ Create and edit RSVP functionality for event owners",
        "✅ Form fields: name, email, phone, status (confirmed/pending/declined)",
        "✅ Validation implemented (email required)",
        "✅ Integration with guest management (create RSVP, then add guests)",
        "❌ Public RSVP form not implemented (the /{locale}/rsvp/:token route is retired until JUB-87; it renders the not-found page, JUB-186)",
        "❌ Public RSVP submission endpoint incomplete (`PublicPages/RsvpController.php` has TODO)",
        "❌ Email notifications not implemented",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Info className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">RSVP Feature Documentation</h1>
          </div>
          <p className="text-muted-foreground text-lg">
            This page documents design decisions, options, and implementation status for the RSVP feature.
            Use the flowcharts below to understand different approaches and make informed decisions.
          </p>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              This page is only visible when <code className="bg-muted px-1 rounded">VITE_ENABLE_DEV_DOCS=true</code> is set.
              It can be disabled in production by removing this environment variable.
            </AlertDescription>
          </Alert>
        </div>

        {/* Current Implementation Status */}
        <Card>
          <CardHeader>
            <CardTitle>Current Implementation Status</CardTitle>
            <CardDescription>
              What's already implemented and what still needs to be built
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {currentOptions.map((option) => (
                <div key={option.feature} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{option.feature}</h3>
                    <Badge
                      variant={
                        option.status === "implemented"
                          ? "default"
                          : option.status === "partially-implemented"
                          ? "secondary"
                          : "destructive"
                      }
                    >
                      {option.status === "implemented" && (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Implemented
                        </>
                      )}
                      {option.status === "partially-implemented" && (
                        <>
                          <Info className="h-3 w-3 mr-1" />
                          Partially Implemented
                        </>
                      )}
                      {option.status === "not-implemented" && (
                        <>
                          <XCircle className="h-3 w-3 mr-1" />
                          Not Implemented
                        </>
                      )}
                    </Badge>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    {option.details.map((detail, idx) => (
                      <li key={idx}>{detail}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Flowcharts */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Design Decision Flowcharts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {flowcharts.map((flow) => (
              <Card
                key={flow.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setActiveFlow(activeFlow === flow.id ? null : flow.id)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{flow.title}</CardTitle>
                    {activeFlow === flow.id ? (
                      <Badge variant="outline">Expanded</Badge>
                    ) : (
                      <Badge variant="secondary">Click to View</Badge>
                    )}
                  </div>
                  <CardDescription>{flow.description}</CardDescription>
                </CardHeader>
                {activeFlow === flow.id && (
                  <CardContent>
                    <div className="bg-muted rounded-lg p-4 overflow-x-auto">
                      <pre className="text-xs font-mono whitespace-pre-wrap">
                        {flow.mermaid}
                      </pre>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">
                      💡 Copy the Mermaid code above and paste it into{" "}
                      <a
                        href="https://mermaid.live"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Mermaid Live Editor
                      </a>{" "}
                      to visualize this flowchart.
                    </p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Key Decisions */}
        <Card>
          <CardHeader>
            <CardTitle>Key Decisions to Make</CardTitle>
            <CardDescription>
              Important questions that need answers before full implementation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="border-l-4 border-primary pl-4 space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Password Protection
                </h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                  <li>Should we support per-guest passwords or keep it simple with one password?</li>
                  <li>How should password sessions work? (localStorage, cookies, server-side sessions?)</li>
                  <li>Should password protection be optional or mandatory for certain event types?</li>
                </ul>
              </div>

              <div className="border-l-4 border-primary pl-4 space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Time-Gating
                </h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                  <li>Do we need a start date, or is deadline-only sufficient?</li>
                  <li>Should we show a countdown timer on the public page?</li>
                  <li>What message should we show after the deadline? (closed, late submissions allowed, etc.)</li>
                  <li>Should late RSVPs be allowed with a warning/penalty?</li>
                </ul>
              </div>

              <div className="border-l-4 border-primary pl-4 space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  Short Links
                </h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                  <li>Should short links expire? (time-based, usage-based, or never?)</li>
                  <li>Should short links pre-fill guest information in the RSVP form?</li>
                  <li>Can one short link be used by multiple people, or should it be one-time use?</li>
                  <li>Should we track analytics per short link? (conversion rates, etc.)</li>
                </ul>
              </div>

              <div className="border-l-4 border-primary pl-4 space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  RSVP Form
                </h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                  <li>What fields are required? (name, email, attending, guests, dietary, accommodation?)</li>
                  <li>Should guests be able to edit their RSVP after submission?</li>
                  <li>How should we handle duplicate submissions? (same email, same short link?)</li>
                  <li>Should we send confirmation emails to guests?</li>
                  <li>Should we send notifications to event owners for each RSVP?</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Implementation Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Implementation Notes</CardTitle>
            <CardDescription>
              Technical details and considerations for developers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Database Schema</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>
                  <code>rsvp_responses</code> table: Stores RSVP SETTINGS per event (one row per event), NOT individual guest responses.
                  <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                    <li><code>rs_date</code> - Response deadline date (event-level setting, when guests should respond by)</li>
                    <li><code>rs_switch</code> - RSVP enabled toggle</li>
                    <li><code>rs_cazare_switch</code> - Accommodation question toggle</li>
                    <li><code>rs_kids_switch</code> - Kids count question toggle</li>
                    <li><code>rs_food_*</code> - Meal option toggles</li>
                    <li><code>rs_question</code> - Custom question text</li>
                    <li><code>rs_mail</code> - Email notification toggle</li>
                  </ul>
                </li>
                <li>
                  <code>guests</code> table: Stores individual guest RSVP responses (multiple guests per RSVP group).
                  <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                    <li><code>guest_submitted_at</code> - When this guest/RSVP group was actually submitted (timestamp)</li>
                    <li><code>guest_rsvp_status</code> - Status (confirmed/pending/declined)</li>
                    <li><code>guest_email</code>, <code>guest_phone</code>, <code>guest_family_name</code> - RSVP group contact info</li>
                    <li><code>guest_rsvp_id</code> - Links multiple guests to the same RSVP response group</li>
                  </ul>
                </li>
                <li>
                  <code>short_links</code> table: Stores short link codes, target URLs, visit tracking
                </li>
                <li>
                  <code>events</code> table: Contains <code>event_password_enabled</code> and <code>event_password</code> fields
                </li>
                <li>
                  <code>guests</code> table: Will store individual RSVP responses (needs verification)
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">API Endpoints</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>
                  <code>{`GET /api/events/public/{slug}`}</code> - Get public event data (includes password protection status)
                </li>
                <li>
                  <code>{`PUT /api/events/{uuid}/rsvp-settings`}</code> - Update RSVP settings (deadline, options)
                </li>
                <li>
                  <code>{`GET /api/events/{uuid}/short-links`}</code> - List short links for an event
                </li>
                <li>
                  <code>{`POST /api/events/{uuid}/short-links`}</code> - Create a new short link
                </li>
                <li>
                  <code>{`GET /api/rsvp/{token}`}</code> - Get RSVP form data (TODO: needs implementation)
                </li>
                <li>
                  <code>{`POST /api/rsvp/{token}`}</code> - Submit RSVP response (TODO: needs implementation)
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Frontend Components</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>
                  <code>RsvpModal.tsx</code> - RSVP form for event owners (✅ fully implemented in GuestsTab)
                </li>
                <li>
                  <code>GuestsTab.tsx</code> - Event owner's guest management with RSVP creation/editing (✅ implemented)
                </li>
                <li>
                  <code>RsvpSettingsAccordion.tsx</code> - Admin settings for RSVP (deadline picker exists)
                </li>
                <li>
                  <code>RsvpPage.tsx</code> - Public RSVP page (❌ placeholder, needs form implementation)
                </li>
                <li>
                  <code>ModernChicRSVP.tsx</code> - RSVP section in modern-chic template (needs button functionality)
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

